import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, MessageSquare, FileText, Settings, LogOut, Menu, X,
  TrendingUp, Eye, Globe, Monitor, Smartphone, Tablet, Clock, RefreshCw,
  ChevronRight, Edit, Trash2, Plus, Upload, Save, ArrowLeft, Calendar,
  ArrowUp, ArrowDown, UserCheck, ChevronLeft, Package, Image, Star, DollarSign,
  BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useAdmin } from '../contexts/AdminContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminDashboard = () => {
  const { isAuthenticated, isLoading: authLoading, logout, token } = useAdmin();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [monthlyAnalytics, setMonthlyAnalytics] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [liveVisitors, setLiveVisitors] = useState({ count: 0, visitors: [] });
  const [contacts, setContacts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [clients, setClients] = useState([]);
  const [heroStats, setHeroStats] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Service editing
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    number: '',
    title: '',
    description: '',
    image: '',
    order: 0,
    active: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  // Package editing
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    price: '',
    category: 'essential',
    features: [],
    popular: false,
    premium: false,
    order: 0,
    active: true
  });
  const [newFeature, setNewFeature] = useState('');

  // Client editing
  const [editingClient, setEditingClient] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: '',
    logo: '',
    website: '',
    order: 0,
    active: true
  });

  // Hero Stats editing
  const [editingHeroStat, setEditingHeroStat] = useState(null);
  const [heroStatForm, setHeroStatForm] = useState({
    key: '',
    value: '',
    order: 0,
    active: true
  });

  // Additional Services editing
  const [additionalServices, setAdditionalServices] = useState([]);
  const [editingAdditionalService, setEditingAdditionalService] = useState(null);
  const [additionalServiceForm, setAdditionalServiceForm] = useState({
    name: '',
    price: '',
    order: 0,
    active: true
  });

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Fetch functions
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/dashboard/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [getAuthHeaders]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/analytics/overview?days=7`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [getAuthHeaders]);

  const fetchMonthlyAnalytics = useCallback(async (year) => {
    try {
      const response = await fetch(`${API}/admin/analytics/monthly?year=${year}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMonthlyAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching monthly analytics:', error);
    }
  }, [getAuthHeaders]);

  const fetchLiveVisitors = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/analytics/live`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLiveVisitors(data);
      }
    } catch (error) {
      console.error('Error fetching live visitors:', error);
    }
  }, [getAuthHeaders]);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/contacts`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, [getAuthHeaders]);

  const fetchInquiries = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/package-inquiries`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInquiries(data);
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    }
  }, [getAuthHeaders]);

  const fetchServices = useCallback(async () => {
    try {
      const response = await fetch(`${API}/services?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await fetch(`${API}/packages?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await fetch(`${API}/clients?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, []);

  const fetchHeroStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/hero-stats?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setHeroStats(data);
      }
    } catch (error) {
      console.error('Error fetching hero stats:', error);
    }
  }, []);

  const fetchAdditionalServices = useCallback(async () => {
    try {
      const response = await fetch(`${API}/additional-services?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setAdditionalServices(data);
      }
    } catch (error) {
      console.error('Error fetching additional services:', error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin');
      return;
    }

    if (isAuthenticated && token) {
      fetchStats();
      fetchAnalytics();
      fetchMonthlyAnalytics(selectedYear);
      fetchLiveVisitors();
      fetchContacts();
      fetchInquiries();
      fetchServices();
      fetchPackages();
      fetchClients();
      fetchHeroStats();
      fetchAdditionalServices();

      // Refresh live visitors every 30 seconds
      const interval = setInterval(fetchLiveVisitors, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, authLoading, token, navigate, fetchStats, fetchAnalytics, fetchMonthlyAnalytics, selectedYear, fetchLiveVisitors, fetchContacts, fetchInquiries, fetchServices, fetchPackages, fetchClients, fetchHeroStats, fetchAdditionalServices]);

  // Service CRUD functions
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setServiceForm(prev => ({ ...prev, image: fullUrl }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading image');
    }
    setUploadingImage(false);
  };

  const saveService = async () => {
    setLoading(true);
    try {
      const url = editingService 
        ? `${API}/admin/services/${editingService.id}`
        : `${API}/admin/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(serviceForm)
      });

      if (response.ok) {
        toast.success(editingService ? 'Service updated!' : 'Service created!');
        setEditingService(null);
        setServiceForm({ number: '', title: '', description: '', image: '', order: 0, active: true });
        fetchServices();
      } else {
        toast.error('Failed to save service');
      }
    } catch (error) {
      toast.error('Error saving service');
    }
    setLoading(false);
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const response = await fetch(`${API}/admin/services/${serviceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Service deleted');
        fetchServices();
      } else {
        toast.error('Failed to delete service');
      }
    } catch (error) {
      toast.error('Error deleting service');
    }
  };

  const updateContactStatus = async (contactId, status) => {
    try {
      const response = await fetch(`${API}/admin/contact/${contactId}/status?status=${status}`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchContacts();
        fetchStats();
      }
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const updateInquiryStatus = async (inquiryId, status) => {
    try {
      const response = await fetch(`${API}/admin/package-inquiry/${inquiryId}/status?status=${status}`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchInquiries();
        fetchStats();
      }
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const seedServices = async () => {
    try {
      const response = await fetch(`${API}/admin/seed-services`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchServices();
      }
    } catch (error) {
      toast.error('Error seeding services');
    }
  };

  // Package CRUD functions
  const handlePackageImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setClientForm(prev => ({ ...prev, logo: fullUrl }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading image');
    }
    setUploadingImage(false);
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setPackageForm(prev => ({
        ...prev,
        features: [...prev.features, { text: newFeature.trim(), included: true }]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setPackageForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const savePackage = async () => {
    setLoading(true);
    try {
      const url = editingPackage
        ? `${API}/admin/packages/${editingPackage.id}`
        : `${API}/admin/packages`;

      const response = await fetch(url, {
        method: editingPackage ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(packageForm)
      });

      if (response.ok) {
        toast.success(editingPackage ? 'Package updated!' : 'Package created!');
        setEditingPackage(null);
        setPackageForm({ name: '', price: '', category: 'essential', features: [], popular: false, premium: false, order: 0, active: true });
        fetchPackages();
      } else {
        toast.error('Failed to save package');
      }
    } catch (error) {
      toast.error('Error saving package');
    }
    setLoading(false);
  };

  const deletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const response = await fetch(`${API}/admin/packages/${packageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Package deleted');
        fetchPackages();
      } else {
        toast.error('Failed to delete package');
      }
    } catch (error) {
      toast.error('Error deleting package');
    }
  };

  const seedPackages = async () => {
    try {
      const response = await fetch(`${API}/admin/seed-packages`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchPackages();
      }
    } catch (error) {
      toast.error('Error seeding packages');
    }
  };

  // Client CRUD functions
  const saveClient = async () => {
    setLoading(true);
    try {
      const url = editingClient
        ? `${API}/admin/clients/${editingClient.id}`
        : `${API}/admin/clients`;

      const response = await fetch(url, {
        method: editingClient ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(clientForm)
      });

      if (response.ok) {
        toast.success(editingClient ? 'Client updated!' : 'Client created!');
        setEditingClient(null);
        setShowClientForm(false);
        setClientForm({ name: '', logo: '', website: '', order: 0, active: true });
        fetchClients();
      } else {
        toast.error('Failed to save client');
      }
    } catch (error) {
      toast.error('Error saving client');
    }
    setLoading(false);
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    try {
      const response = await fetch(`${API}/admin/clients/${clientId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Client deleted');
        fetchClients();
      } else {
        toast.error('Failed to delete client');
      }
    } catch (error) {
      toast.error('Error deleting client');
    }
  };

  const seedClients = async () => {
    try {
      const response = await fetch(`${API}/admin/seed-clients`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchClients();
      }
    } catch (error) {
      toast.error('Error seeding clients');
    }
  };

  // Hero Stats CRUD functions
  const saveHeroStat = async () => {
    setLoading(true);
    try {
      const url = editingHeroStat
        ? `${API}/admin/hero-stats/${editingHeroStat.id}`
        : `${API}/admin/hero-stats`;

      const response = await fetch(url, {
        method: editingHeroStat ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(heroStatForm)
      });

      if (response.ok) {
        toast.success(editingHeroStat ? 'Stat updated!' : 'Stat created!');
        setEditingHeroStat(null);
        setHeroStatForm({ key: '', value: '', order: 0, active: true });
        fetchHeroStats();
      } else {
        toast.error('Failed to save stat');
      }
    } catch (error) {
      toast.error('Error saving stat');
    }
    setLoading(false);
  };

  const deleteHeroStat = async (statId) => {
    if (!window.confirm('Are you sure you want to delete this stat?')) return;

    try {
      const response = await fetch(`${API}/admin/hero-stats/${statId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Stat deleted');
        fetchHeroStats();
      } else {
        toast.error('Failed to delete stat');
      }
    } catch (error) {
      toast.error('Error deleting stat');
    }
  };

  const seedHeroStats = async () => {
    try {
      const response = await fetch(`${API}/admin/seed-hero-stats`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchHeroStats();
      }
    } catch (error) {
      toast.error('Error seeding hero stats');
    }
  };

  // Additional Services CRUD functions
  const saveAdditionalService = async () => {
    setLoading(true);
    try {
      const url = editingAdditionalService
        ? `${API}/admin/additional-services/${editingAdditionalService.id}`
        : `${API}/admin/additional-services`;

      const response = await fetch(url, {
        method: editingAdditionalService ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(additionalServiceForm)
      });

      if (response.ok) {
        toast.success(editingAdditionalService ? 'Service updated!' : 'Service created!');
        setEditingAdditionalService(null);
        setAdditionalServiceForm({ name: '', price: '', order: 0, active: true });
        fetchAdditionalServices();
      } else {
        toast.error('Failed to save service');
      }
    } catch (error) {
      toast.error('Error saving service');
    }
    setLoading(false);
  };

  const deleteAdditionalService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      const response = await fetch(`${API}/admin/additional-services/${serviceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Service deleted');
        fetchAdditionalServices();
      } else {
        toast.error('Failed to delete service');
      }
    } catch (error) {
      toast.error('Error deleting service');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'live', label: 'Live Visitors', icon: Eye },
    { id: 'contacts', label: 'Contacts', icon: MessageSquare },
    { id: 'inquiries', label: 'Inquiries', icon: FileText },
    { id: 'packages', label: 'Packages', icon: Package },
    { id: 'additionalservices', label: 'Add. Services', icon: DollarSign },
    { id: 'clients', label: 'Clients', icon: Image },
    { id: 'herostats', label: 'Hero Stats', icon: BarChart3 },
    { id: 'services', label: 'Services', icon: Settings },
  ];

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return <Smartphone size={16} />;
      case 'tablet': return <Tablet size={16} />;
      default: return <Monitor size={16} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 border-r border-slate-800 transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          {sidebarOpen && <span className="text-white font-bold text-lg">WIM Admin</span>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white p-2"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                  : 'text-gray-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-all duration-200"
            data-testid="logout-button"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6 md:p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6" data-testid="overview-tab">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white">Dashboard Overview</h1>
                <Button onClick={fetchStats} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                  <RefreshCw size={16} className="mr-2" /> Refresh
                </Button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">Live Visitors</p>
                        <p className="text-3xl font-bold text-green-400" data-testid="live-visitors-count">{stats?.live_visitors || 0}</p>
                      </div>
                      <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                        <Eye className="text-green-400" size={24} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">Today's Visitors</p>
                        <p className="text-3xl font-bold text-blue-400" data-testid="today-visitors-count">{stats?.today_visitors || 0}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <Users className="text-blue-400" size={24} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/80 border-slate-800 border-purple-500/30">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">Unique Today</p>
                        <p className="text-3xl font-bold text-purple-400" data-testid="unique-visitors-count">{stats?.unique_visitors_today || 0}</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <UserCheck className="text-purple-400" size={24} />
                      </div>
                    </div>
                    <p className="text-gray-500 text-sm mt-2">By unique IP</p>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">New Contacts</p>
                        <p className="text-3xl font-bold text-pink-400" data-testid="new-contacts-count">{stats?.new_contacts || 0}</p>
                      </div>
                      <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center">
                        <MessageSquare className="text-pink-400" size={24} />
                      </div>
                    </div>
                    <p className="text-gray-500 text-sm mt-2">Total: {stats?.total_contacts || 0}</p>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">Pending Inquiries</p>
                        <p className="text-3xl font-bold text-yellow-400" data-testid="pending-inquiries-count">{stats?.pending_inquiries || 0}</p>
                      </div>
                      <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                        <FileText className="text-yellow-400" size={24} />
                      </div>
                    </div>
                    <p className="text-gray-500 text-sm mt-2">Total: {stats?.total_inquiries || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-slate-900/80 border-slate-800 hover:border-pink-500/50 transition-colors cursor-pointer" onClick={() => setActiveTab('contacts')}>
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">View Contacts</p>
                      <p className="text-gray-400 text-sm">Manage contact submissions</p>
                    </div>
                    <ChevronRight className="text-gray-400" />
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/80 border-slate-800 hover:border-pink-500/50 transition-colors cursor-pointer" onClick={() => setActiveTab('services')}>
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">Edit Services</p>
                      <p className="text-gray-400 text-sm">Manage your services</p>
                    </div>
                    <ChevronRight className="text-gray-400" />
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/80 border-slate-800 hover:border-pink-500/50 transition-colors cursor-pointer" onClick={() => setActiveTab('analytics')}>
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">View Analytics</p>
                      <p className="text-gray-400 text-sm">Detailed traffic data</p>
                    </div>
                    <ChevronRight className="text-gray-400" />
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6" data-testid="analytics-tab">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white">Traffic Analytics</h1>
                <Button onClick={fetchAnalytics} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                  <RefreshCw size={16} className="mr-2" /> Refresh
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <p className="text-gray-400 text-sm">Total Sessions (7 days)</p>
                    <p className="text-3xl font-bold text-white">{analytics?.total_sessions || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <p className="text-gray-400 text-sm">Page Views (7 days)</p>
                    <p className="text-3xl font-bold text-white">{analytics?.total_page_views || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <p className="text-gray-400 text-sm">Unique Visitors (7 days)</p>
                    <p className="text-3xl font-bold text-white">{analytics?.unique_visitors || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Breakdowns */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Device Breakdown */}
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Device Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {analytics?.device_breakdown?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-300">
                          {getDeviceIcon(item.device)}
                          <span className="capitalize">{item.device || 'Unknown'}</span>
                        </div>
                        <span className="text-white font-semibold">{item.count}</span>
                      </div>
                    )) || <p className="text-gray-500">No data</p>}
                  </CardContent>
                </Card>

                {/* Browser Breakdown */}
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Top Browsers</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {analytics?.browser_breakdown?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-gray-300">{item.browser || 'Unknown'}</span>
                        <span className="text-white font-semibold">{item.count}</span>
                      </div>
                    )) || <p className="text-gray-500">No data</p>}
                  </CardContent>
                </Card>

                {/* OS Breakdown */}
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Operating Systems</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {analytics?.os_breakdown?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-gray-300">{item.os || 'Unknown'}</span>
                        <span className="text-white font-semibold">{item.count}</span>
                      </div>
                    )) || <p className="text-gray-500">No data</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Top Pages & Referrers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Top Pages</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {analytics?.top_pages?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-gray-300 truncate max-w-[200px]">{item.page}</span>
                        <span className="text-white font-semibold">{item.views} views</span>
                      </div>
                    )) || <p className="text-gray-500">No data</p>}
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/80 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Top Referrers</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {analytics?.top_referrers?.length > 0 ? analytics.top_referrers.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-gray-300 truncate max-w-[200px]">{item.referrer}</span>
                        <span className="text-white font-semibold">{item.count}</span>
                      </div>
                    )) : <p className="text-gray-500">No referrer data</p>}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Calendar Analytics Tab */}
          {activeTab === 'calendar' && (
            <div className="space-y-6" data-testid="calendar-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white">Calendar Analytics</h1>
                  <p className="text-gray-400 mt-1">Track visitor improvements month over month</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        const newYear = selectedYear - 1;
                        setSelectedYear(newYear);
                        fetchMonthlyAnalytics(newYear);
                      }}
                      className="border-slate-700 text-gray-400 hover:text-white"
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="text-white font-bold text-xl px-4">{selectedYear}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        const newYear = selectedYear + 1;
                        setSelectedYear(newYear);
                        fetchMonthlyAnalytics(newYear);
                      }}
                      className="border-slate-700 text-gray-400 hover:text-white"
                      disabled={selectedYear >= new Date().getFullYear()}
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                  <Button onClick={() => fetchMonthlyAnalytics(selectedYear)} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                    <RefreshCw size={16} className="mr-2" /> Refresh
                  </Button>
                </div>
              </div>

              {/* Monthly Overview Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {monthlyAnalytics?.monthly_data?.map((month, idx) => {
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const isCurrentMonth = month.month === monthlyAnalytics?.current_month && month.year === monthlyAnalytics?.current_year;
                  const hasData = month.unique_visitors > 0 || month.total_sessions > 0;
                  
                  return (
                    <Card 
                      key={idx} 
                      className={`bg-slate-900/80 border-slate-800 transition-all duration-300 ${
                        isCurrentMonth ? 'border-pink-500/50 ring-1 ring-pink-500/30' : ''
                      } ${hasData ? 'hover:border-pink-500/30' : 'opacity-60'}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className={`text-lg font-bold ${isCurrentMonth ? 'text-pink-400' : 'text-white'}`}>
                            {monthNames[month.month - 1]}
                          </span>
                          {month.growth_percentage !== 0 && month.growth_percentage !== undefined && (
                            <div className={`flex items-center gap-1 text-sm ${
                              month.growth_percentage > 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {month.growth_percentage > 0 ? (
                                <ArrowUp size={14} />
                              ) : (
                                <ArrowDown size={14} />
                              )}
                              <span>{Math.abs(month.growth_percentage)}%</span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Unique</span>
                            <span className="text-purple-400 font-semibold">{month.unique_visitors}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Sessions</span>
                            <span className="text-blue-400 font-semibold">{month.total_sessions}</span>
                          </div>
                        </div>
                        {/* Progress bar showing relative activity */}
                        <div className="mt-3 h-1 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-500"
                            style={{ 
                              width: `${monthlyAnalytics?.monthly_data ? 
                                Math.min(100, (month.unique_visitors / Math.max(...monthlyAnalytics.monthly_data.map(m => m.unique_visitors || 1))) * 100) : 0}%` 
                            }}
                          ></div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Current Month Daily Breakdown */}
              {monthlyAnalytics?.daily_data && monthlyAnalytics.daily_data.length > 0 && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Calendar className="text-pink-400" size={20} />
                      Daily Breakdown - {new Date().toLocaleString('default', { month: 'long' })} {new Date().getFullYear()}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-7 gap-2">
                      {/* Day headers */}
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-gray-500 text-xs font-medium py-1">
                          {day}
                        </div>
                      ))}
                      
                      {/* Calendar days */}
                      {(() => {
                        const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getDay();
                        const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
                        const today = new Date().getDate();
                        
                        // Empty cells for days before first of month
                        const emptyCells = Array(firstDay).fill(null);
                        
                        // Days of the month
                        const dayCells = Array.from({ length: daysInMonth }, (_, i) => {
                          const day = i + 1;
                          const dateStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const dayData = monthlyAnalytics.daily_data.find(d => d.date === dateStr);
                          const maxVisitors = Math.max(...monthlyAnalytics.daily_data.map(d => d.unique_visitors || 0), 1);
                          const intensity = dayData ? (dayData.unique_visitors / maxVisitors) : 0;
                          
                          return { day, dayData, intensity, isToday: day === today };
                        });
                        
                        return [...emptyCells, ...dayCells].map((item, idx) => {
                          if (item === null) {
                            return <div key={`empty-${idx}`} className="aspect-square"></div>;
                          }
                          
                          const { day, dayData, intensity, isToday } = item;
                          
                          return (
                            <div 
                              key={day}
                              className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs transition-all duration-200 cursor-default
                                ${isToday ? 'ring-2 ring-pink-500' : ''}
                                ${intensity > 0.7 ? 'bg-pink-500/60 text-white' : 
                                  intensity > 0.4 ? 'bg-pink-500/40 text-white' : 
                                  intensity > 0 ? 'bg-pink-500/20 text-gray-300' : 
                                  'bg-slate-800/50 text-gray-500'}`}
                              title={dayData ? `${dayData.unique_visitors} unique, ${dayData.sessions} sessions` : 'No data'}
                            >
                              <span className="font-medium">{day}</span>
                              {dayData && dayData.unique_visitors > 0 && (
                                <span className="text-[10px] opacity-80">{dayData.unique_visitors}</span>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-slate-800">
                      <span className="text-gray-500 text-xs">Less</span>
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded bg-slate-800/50"></div>
                        <div className="w-4 h-4 rounded bg-pink-500/20"></div>
                        <div className="w-4 h-4 rounded bg-pink-500/40"></div>
                        <div className="w-4 h-4 rounded bg-pink-500/60"></div>
                      </div>
                      <span className="text-gray-500 text-xs">More</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Year Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <p className="text-gray-400 text-sm">Total Unique Visitors ({selectedYear})</p>
                    <p className="text-3xl font-bold text-purple-400">
                      {monthlyAnalytics?.monthly_data?.reduce((sum, m) => sum + (m.unique_visitors || 0), 0) || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <p className="text-gray-400 text-sm">Total Sessions ({selectedYear})</p>
                    <p className="text-3xl font-bold text-blue-400">
                      {monthlyAnalytics?.monthly_data?.reduce((sum, m) => sum + (m.total_sessions || 0), 0) || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-6">
                    <p className="text-gray-400 text-sm">Best Month</p>
                    <p className="text-3xl font-bold text-pink-400">
                      {(() => {
                        const best = monthlyAnalytics?.monthly_data?.reduce((max, m) => 
                          (m.unique_visitors || 0) > (max.unique_visitors || 0) ? m : max
                        , { unique_visitors: 0, month: 0 });
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        return best?.month ? monthNames[best.month - 1] : 'N/A';
                      })()}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Live Visitors Tab */}
          {activeTab === 'live' && (
            <div className="space-y-6" data-testid="live-visitors-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white">Live Visitors</h1>
                  <p className="text-gray-400 mt-1">Currently active on your site</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-bold text-xl">{liveVisitors.count}</span>
                    <span className="text-gray-400">online</span>
                  </div>
                  <Button onClick={fetchLiveVisitors} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                    <RefreshCw size={16} className="mr-2" /> Refresh
                  </Button>
                </div>
              </div>

              {liveVisitors.visitors.length > 0 ? (
                <div className="grid gap-4">
                  {liveVisitors.visitors.map((visitor, idx) => (
                    <Card key={idx} className="bg-slate-900/80 border-slate-800">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-blue-500 rounded-full flex items-center justify-center">
                              {getDeviceIcon(visitor.device_type)}
                            </div>
                            <div>
                              <p className="text-white font-semibold">{visitor.browser} on {visitor.os}</p>
                              <p className="text-gray-400 text-sm">{visitor.ip_address}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div>
                              <p className="text-gray-400">Entry Page</p>
                              <p className="text-white">{visitor.entry_page}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Pages Viewed</p>
                              <p className="text-white">{visitor.pages_visited?.length || 1}</p>
                            </div>
                            {visitor.referrer && (
                              <div>
                                <p className="text-gray-400">Referrer</p>
                                <p className="text-white truncate max-w-[150px]">{visitor.referrer}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-12 text-center">
                    <Globe className="mx-auto text-gray-600 mb-4" size={48} />
                    <p className="text-gray-400">No active visitors right now</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-6" data-testid="contacts-tab">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white">Contact Submissions</h1>
                <Button onClick={fetchContacts} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                  <RefreshCw size={16} className="mr-2" /> Refresh
                </Button>
              </div>

              {contacts.length > 0 ? (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <Card key={contact.id} className="bg-slate-900/80 border-slate-800">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between flex-wrap gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-white font-semibold text-lg">{contact.name}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                contact.status === 'new' ? 'bg-green-500/20 text-green-400' :
                                contact.status === 'read' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-blue-500/20 text-blue-400'
                              }`}>
                                {contact.status}
                              </span>
                            </div>
                            <p className="text-gray-400 text-sm mb-1">{contact.email} {contact.phone && `• ${contact.phone}`}</p>
                            <p className="text-gray-300 mt-3">{contact.message}</p>
                            <p className="text-gray-500 text-sm mt-3 flex items-center gap-1">
                              <Clock size={14} />
                              {new Date(contact.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <select
                              value={contact.status}
                              onChange={(e) => updateContactStatus(contact.id, e.target.value)}
                              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                            >
                              <option value="new">New</option>
                              <option value="read">Read</option>
                              <option value="responded">Responded</option>
                            </select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-12 text-center">
                    <MessageSquare className="mx-auto text-gray-600 mb-4" size={48} />
                    <p className="text-gray-400">No contact submissions yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Inquiries Tab */}
          {activeTab === 'inquiries' && (
            <div className="space-y-6" data-testid="inquiries-tab">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white">Package Inquiries</h1>
                <Button onClick={fetchInquiries} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                  <RefreshCw size={16} className="mr-2" /> Refresh
                </Button>
              </div>

              {inquiries.length > 0 ? (
                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <Card key={inquiry.id} className="bg-slate-900/80 border-slate-800">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between flex-wrap gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-white font-semibold text-lg">{inquiry.customer_name}</h3>
                              <span className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-xs font-medium">
                                {inquiry.package_name}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                inquiry.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                inquiry.status === 'contacted' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-green-500/20 text-green-400'
                              }`}>
                                {inquiry.status}
                              </span>
                            </div>
                            <p className="text-gray-400 text-sm mb-1">{inquiry.customer_email} {inquiry.customer_phone && `• ${inquiry.customer_phone}`}</p>
                            <p className="text-white font-semibold mt-2">Package Price: {inquiry.package_price}</p>
                            {inquiry.message && <p className="text-gray-300 mt-3">{inquiry.message}</p>}
                            <p className="text-gray-500 text-sm mt-3 flex items-center gap-1">
                              <Clock size={14} />
                              {new Date(inquiry.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <select
                              value={inquiry.status}
                              onChange={(e) => updateInquiryStatus(inquiry.id, e.target.value)}
                              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-12 text-center">
                    <FileText className="mx-auto text-gray-600 mb-4" size={48} />
                    <p className="text-gray-400">No package inquiries yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="space-y-6" data-testid="services-tab">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white">Manage Services</h1>
                <div className="flex gap-2">
                  {services.length === 0 && (
                    <Button onClick={seedServices} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                      Seed Default Services
                    </Button>
                  )}
                  <Button 
                    onClick={() => {
                      setEditingService(null);
                      setServiceForm({ number: '', title: '', description: '', image: '', order: services.length + 1, active: true });
                    }}
                    className="bg-pink-500 hover:bg-pink-600"
                  >
                    <Plus size={16} className="mr-2" /> Add Service
                  </Button>
                </div>
              </div>

              {/* Service Form */}
              {(editingService !== null || serviceForm.title !== '' || serviceForm.number !== '') && (
                <Card className="bg-slate-900/80 border-pink-500/50">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center justify-between">
                      <span>{editingService ? 'Edit Service' : 'New Service'}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setEditingService(null);
                          setServiceForm({ number: '', title: '', description: '', image: '', order: 0, active: true });
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <X size={20} />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Number (e.g., 01)</label>
                        <Input
                          value={serviceForm.number}
                          onChange={(e) => setServiceForm(prev => ({ ...prev, number: e.target.value }))}
                          className="bg-slate-800 border-slate-700 text-white"
                          placeholder="01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Order</label>
                        <Input
                          type="number"
                          value={serviceForm.order}
                          onChange={(e) => setServiceForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                      <Input
                        value={serviceForm.title}
                        onChange={(e) => setServiceForm(prev => ({ ...prev, title: e.target.value }))}
                        className="bg-slate-800 border-slate-700 text-white"
                        placeholder="Service Title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                      <Textarea
                        value={serviceForm.description}
                        onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
                        className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
                        placeholder="Service description..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Image</label>
                      <div className="flex gap-4 items-start">
                        <div className="flex-1">
                          <Input
                            value={serviceForm.image}
                            onChange={(e) => setServiceForm(prev => ({ ...prev, image: e.target.value }))}
                            className="bg-slate-800 border-slate-700 text-white"
                            placeholder="Image URL or upload"
                          />
                          <div className="mt-2">
                            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-gray-300 hover:bg-slate-700 transition-colors">
                              <Upload size={16} />
                              {uploadingImage ? 'Uploading...' : 'Upload Image'}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                                disabled={uploadingImage}
                              />
                            </label>
                          </div>
                        </div>
                        {serviceForm.image && (
                          <img src={serviceForm.image} alt="Preview" className="w-24 h-24 object-cover rounded-lg border border-slate-700" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="active"
                        checked={serviceForm.active}
                        onChange={(e) => setServiceForm(prev => ({ ...prev, active: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-pink-500"
                      />
                      <label htmlFor="active" className="text-gray-300">Active (visible on website)</label>
                    </div>
                    <Button onClick={saveService} disabled={loading} className="bg-pink-500 hover:bg-pink-600 w-full">
                      <Save size={16} className="mr-2" />
                      {loading ? 'Saving...' : 'Save Service'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Services List */}
              <div className="grid gap-4">
                {services.map((service) => (
                  <Card key={service.id} className={`bg-slate-900/80 border-slate-800 ${!service.active && 'opacity-60'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <img src={service.image} alt={service.title} className="w-20 h-20 object-cover rounded-lg" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-pink-400 font-bold">{service.number}</span>
                            <h3 className="text-white font-semibold">{service.title}</h3>
                            {!service.active && <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded">Hidden</span>}
                          </div>
                          <p className="text-gray-400 text-sm line-clamp-2">{service.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingService(service);
                              setServiceForm({
                                number: service.number,
                                title: service.title,
                                description: service.description,
                                image: service.image,
                                order: service.order,
                                active: service.active
                              });
                            }}
                            className="border-slate-700 text-gray-400 hover:text-white"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteService(service.id)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {services.length === 0 && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-12 text-center">
                    <Settings className="mx-auto text-gray-600 mb-4" size={48} />
                    <p className="text-gray-400 mb-4">No services yet. Add your first service or seed default data.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Packages Tab */}
          {activeTab === 'packages' && (
            <div className="space-y-6" data-testid="packages-tab">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white">Manage Pricing Packages</h1>
                <div className="flex gap-2">
                  {packages.length === 0 && (
                    <Button onClick={seedPackages} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                      Seed Default Packages
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setEditingPackage(null);
                      setPackageForm({ name: '', price: '', category: 'essential', features: [], popular: false, premium: false, order: packages.length + 1, active: true });
                    }}
                    className="bg-pink-500 hover:bg-pink-600"
                  >
                    <Plus size={16} className="mr-2" /> Add Package
                  </Button>
                </div>
              </div>

              {/* Package Form */}
              {(editingPackage !== null || packageForm.name !== '') && (
                <Card className="bg-slate-900/80 border-pink-500/50">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center justify-between">
                      <span>{editingPackage ? 'Edit Package' : 'New Package'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingPackage(null);
                          setPackageForm({ name: '', price: '', category: 'essential', features: [], popular: false, premium: false, order: 0, active: true });
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <X size={20} />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Package Name</label>
                        <Input
                          value={packageForm.name}
                          onChange={(e) => setPackageForm(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-slate-800 border-slate-700 text-white"
                          placeholder="Essential Gold"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Price (€)</label>
                        <Input
                          value={packageForm.price}
                          onChange={(e) => setPackageForm(prev => ({ ...prev, price: e.target.value }))}
                          className="bg-slate-800 border-slate-700 text-white"
                          placeholder="3000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                        <select
                          value={packageForm.category}
                          onChange={(e) => setPackageForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                        >
                          <option value="essential">Essential</option>
                          <option value="prestige">Prestige</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Features</label>
                      <div className="flex gap-2 mb-2">
                        <Input
                          value={newFeature}
                          onChange={(e) => setNewFeature(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                          className="bg-slate-800 border-slate-700 text-white"
                          placeholder="Add a feature..."
                        />
                        <Button onClick={addFeature} variant="outline" className="border-slate-700 text-gray-400 hover:text-white">
                          <Plus size={16} />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {packageForm.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-2">
                            <span className="text-gray-300 flex-1">{feature.text}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFeature(index)}
                              className="text-red-400 hover:text-red-300 p-1"
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="popular"
                          checked={packageForm.popular}
                          onChange={(e) => setPackageForm(prev => ({ ...prev, popular: e.target.checked, premium: e.target.checked ? false : prev.premium }))}
                          className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-pink-500"
                        />
                        <label htmlFor="popular" className="text-gray-300">Popular Badge</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="premium"
                          checked={packageForm.premium}
                          onChange={(e) => setPackageForm(prev => ({ ...prev, premium: e.target.checked, popular: e.target.checked ? false : prev.popular }))}
                          className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-blue-500"
                        />
                        <label htmlFor="premium" className="text-gray-300">Premium Badge</label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Order</label>
                        <Input
                          type="number"
                          value={packageForm.order}
                          onChange={(e) => setPackageForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="pkgActive"
                          checked={packageForm.active}
                          onChange={(e) => setPackageForm(prev => ({ ...prev, active: e.target.checked }))}
                          className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-pink-500"
                        />
                        <label htmlFor="pkgActive" className="text-gray-300">Active</label>
                      </div>
                    </div>

                    <Button onClick={savePackage} disabled={loading} className="bg-pink-500 hover:bg-pink-600 w-full">
                      <Save size={16} className="mr-2" />
                      {loading ? 'Saving...' : 'Save Package'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Essential Packages */}
              <div>
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Package className="text-pink-400" size={20} />
                  Essential Packages
                </h2>
                <div className="grid md:grid-cols-3 gap-4">
                  {packages.filter(p => p.category === 'essential').map((pkg) => (
                    <Card key={pkg.id} className={`bg-slate-900/80 border-slate-800 ${!pkg.active && 'opacity-60'} ${pkg.popular && 'border-pink-500/50'} ${pkg.premium && 'border-blue-500/50'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-white font-semibold">{pkg.name}</h3>
                          <div className="flex gap-1">
                            {pkg.popular && <Star className="text-pink-400" size={16} />}
                            {pkg.premium && <Star className="text-blue-400" size={16} />}
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-pink-400 mb-3">{pkg.price}€</p>
                        <p className="text-gray-400 text-sm mb-3">{pkg.features.length} features</p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingPackage(pkg);
                              setPackageForm({
                                name: pkg.name,
                                price: pkg.price,
                                category: pkg.category,
                                features: pkg.features || [],
                                popular: pkg.popular,
                                premium: pkg.premium,
                                order: pkg.order,
                                active: pkg.active
                              });
                            }}
                            className="border-slate-700 text-gray-400 hover:text-white flex-1"
                          >
                            <Edit size={14} className="mr-1" /> Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deletePackage(pkg.id)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Prestige Packages */}
              <div>
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Package className="text-blue-400" size={20} />
                  Prestige Packages
                </h2>
                <div className="grid md:grid-cols-3 gap-4">
                  {packages.filter(p => p.category === 'prestige').map((pkg) => (
                    <Card key={pkg.id} className={`bg-slate-900/80 border-slate-800 ${!pkg.active && 'opacity-60'} ${pkg.popular && 'border-pink-500/50'} ${pkg.premium && 'border-blue-500/50'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-white font-semibold">{pkg.name}</h3>
                          <div className="flex gap-1">
                            {pkg.popular && <Star className="text-pink-400" size={16} />}
                            {pkg.premium && <Star className="text-blue-400" size={16} />}
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-blue-400 mb-3">{pkg.price}€</p>
                        <p className="text-gray-400 text-sm mb-3">{pkg.features.length} features</p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingPackage(pkg);
                              setPackageForm({
                                name: pkg.name,
                                price: pkg.price,
                                category: pkg.category,
                                features: pkg.features || [],
                                popular: pkg.popular,
                                premium: pkg.premium,
                                order: pkg.order,
                                active: pkg.active
                              });
                            }}
                            className="border-slate-700 text-gray-400 hover:text-white flex-1"
                          >
                            <Edit size={14} className="mr-1" /> Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deletePackage(pkg.id)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {packages.length === 0 && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-12 text-center">
                    <Package className="mx-auto text-gray-600 mb-4" size={48} />
                    <p className="text-gray-400 mb-4">No packages yet. Add your first package or seed default data.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Additional Services Tab */}
          {activeTab === 'additionalservices' && (
            <div className="space-y-6" data-testid="additionalservices-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white">Additional Services</h1>
                  <p className="text-gray-400 mt-1">Manage the additional services shown on the pricing page</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API}/admin/seed-additional-services`, {
                          method: 'POST',
                          headers: getAuthHeaders()
                        });
                        if (response.ok) {
                          const data = await response.json();
                          toast.success(data.message);
                          fetchAdditionalServices();
                        }
                      } catch (error) {
                        toast.error('Error seeding additional services');
                      }
                    }}
                    variant="outline"
                    className="border-slate-700 text-gray-300"
                  >
                    Seed Default
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingAdditionalService(null);
                      setAdditionalServiceForm({ name: '', price: '', order: additionalServices.length + 1, active: true });
                    }}
                    className="bg-pink-500 hover:bg-pink-600"
                  >
                    <Plus size={16} className="mr-2" /> Add Service
                  </Button>
                </div>
              </div>

              {/* Additional Service Form */}
              {(editingAdditionalService !== null || additionalServiceForm.name !== '') && (
                <Card className="bg-slate-900/80 border-pink-500/50">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center justify-between">
                      <span>{editingAdditionalService ? 'Edit Service' : 'New Service'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingAdditionalService(null);
                          setAdditionalServiceForm({ name: '', price: '', order: 0, active: true });
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <X size={20} />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Service Name</label>
                        <Input
                          value={additionalServiceForm.name}
                          onChange={(e) => setAdditionalServiceForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Presentation Website"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Price</label>
                        <Input
                          value={additionalServiceForm.price}
                          onChange={(e) => setAdditionalServiceForm(prev => ({ ...prev, price: e.target.value }))}
                          placeholder="e.g., +2000 €"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Order</label>
                        <Input
                          type="number"
                          value={additionalServiceForm.order}
                          onChange={(e) => setAdditionalServiceForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={additionalServiceForm.active}
                          onChange={(e) => setAdditionalServiceForm(prev => ({ ...prev, active: e.target.checked }))}
                          className="mr-2 w-4 h-4"
                        />
                        <label className="text-gray-300">Active</label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingAdditionalService(null);
                          setAdditionalServiceForm({ name: '', price: '', order: 0, active: true });
                        }}
                        className="border-slate-700 text-gray-300"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={saveAdditionalService}
                        disabled={loading || !additionalServiceForm.name || !additionalServiceForm.price}
                        className="bg-pink-500 hover:bg-pink-600"
                      >
                        <Save size={16} className="mr-2" />
                        {editingAdditionalService ? 'Update' : 'Create'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Additional Services List */}
              {additionalServices.length > 0 && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-800">
                      {additionalServices.map((service) => (
                        <div key={service.id} className="p-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center text-gray-400 text-sm">
                              {service.order}
                            </div>
                            <div>
                              <h4 className="text-white font-medium">{service.name}</h4>
                              <p className="text-pink-400 text-sm">{service.price}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`px-2 py-1 rounded text-xs ${service.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {service.active ? 'Active' : 'Inactive'}
                            </span>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingAdditionalService(service);
                                  setAdditionalServiceForm({
                                    name: service.name,
                                    price: service.price,
                                    order: service.order,
                                    active: service.active
                                  });
                                }}
                                className="text-gray-400 hover:text-white"
                              >
                                <Edit size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteAdditionalService(service.id)}
                                className="text-gray-400 hover:text-red-400"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {additionalServices.length === 0 && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-12 text-center">
                    <DollarSign className="mx-auto text-gray-600 mb-4" size={48} />
                    <p className="text-gray-400 mb-4">No additional services yet. Add your first service.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Clients Tab */}
          {activeTab === 'clients' && (
            <div className="space-y-6" data-testid="clients-tab">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white">Manage Client Logos</h1>
                <div className="flex gap-2">
                  {clients.length === 0 && (
                    <Button onClick={seedClients} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                      Seed Default Clients
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setEditingClient(null);
                      setShowClientForm(true);
                      setClientForm({ name: '', logo: '', website: '', order: clients.length + 1, active: true });
                    }}
                    className="bg-pink-500 hover:bg-pink-600"
                  >
                    <Plus size={16} className="mr-2" /> Add Client
                  </Button>
                </div>
              </div>

              {/* Client Form */}
              {(editingClient !== null || showClientForm) && (
                <Card className="bg-slate-900/80 border-pink-500/50">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center justify-between">
                      <span>{editingClient ? 'Edit Client' : 'New Client'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingClient(null);
                          setShowClientForm(false);
                          setClientForm({ name: '', logo: '', website: '', order: 0, active: true });
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <X size={20} />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Client Name</label>
                        <Input
                          value={clientForm.name}
                          onChange={(e) => setClientForm(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-slate-800 border-slate-700 text-white"
                          placeholder="Client Name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Website (optional)</label>
                        <Input
                          value={clientForm.website}
                          onChange={(e) => setClientForm(prev => ({ ...prev, website: e.target.value }))}
                          className="bg-slate-800 border-slate-700 text-white"
                          placeholder="https://example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Logo</label>
                      <div className="flex gap-4 items-start">
                        <div className="flex-1">
                          <Input
                            value={clientForm.logo}
                            onChange={(e) => setClientForm(prev => ({ ...prev, logo: e.target.value }))}
                            className="bg-slate-800 border-slate-700 text-white"
                            placeholder="Logo URL or upload"
                          />
                          <div className="mt-2">
                            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-gray-300 hover:bg-slate-700 transition-colors">
                              <Upload size={16} />
                              {uploadingImage ? 'Uploading...' : 'Upload Logo'}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handlePackageImageUpload}
                                className="hidden"
                                disabled={uploadingImage}
                              />
                            </label>
                          </div>
                        </div>
                        {clientForm.logo && (
                          <img src={clientForm.logo} alt="Preview" className="w-24 h-24 object-contain rounded-lg border border-slate-700 bg-white p-2" />
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Order</label>
                        <Input
                          type="number"
                          value={clientForm.order}
                          onChange={(e) => setClientForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-8">
                        <input
                          type="checkbox"
                          id="clientActive"
                          checked={clientForm.active}
                          onChange={(e) => setClientForm(prev => ({ ...prev, active: e.target.checked }))}
                          className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-pink-500"
                        />
                        <label htmlFor="clientActive" className="text-gray-300">Active (visible on website)</label>
                      </div>
                    </div>

                    <Button onClick={saveClient} disabled={loading} className="bg-pink-500 hover:bg-pink-600 w-full">
                      <Save size={16} className="mr-2" />
                      {loading ? 'Saving...' : 'Save Client'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Clients Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {clients.map((client) => (
                  <Card key={client.id} className={`bg-slate-900/80 border-slate-800 ${!client.active && 'opacity-60'}`}>
                    <CardContent className="p-4">
                      <div className="aspect-square bg-white rounded-lg flex items-center justify-center p-4 mb-3">
                        <img src={client.logo} alt={client.name} className="max-w-full max-h-full object-contain" />
                      </div>
                      <h3 className="text-white font-semibold text-center mb-3 truncate">{client.name}</h3>
                      {!client.active && (
                        <p className="text-center text-xs text-gray-500 mb-2">Hidden</p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingClient(client);
                            setShowClientForm(true);
                            setClientForm({
                              name: client.name,
                              logo: client.logo,
                              website: client.website || '',
                              order: client.order,
                              active: client.active
                            });
                          }}
                          className="border-slate-700 text-gray-400 hover:text-white flex-1"
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteClient(client.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {clients.length === 0 && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-12 text-center">
                    <Image className="mx-auto text-gray-600 mb-4" size={48} />
                    <p className="text-gray-400 mb-4">No clients yet. Add your first client or seed default data.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Hero Stats Tab */}
          {activeTab === 'herostats' && (
            <div className="space-y-6" data-testid="herostats-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white">Hero Section Stats</h1>
                  <p className="text-gray-400 mt-1">Manage the statistics displayed in the hero section (Years, Clients, Projects)</p>
                </div>
                <div className="flex gap-2">
                  {heroStats.length === 0 && (
                    <Button onClick={seedHeroStats} variant="outline" size="sm" className="border-slate-700 text-gray-400 hover:text-white">
                      Seed Default Stats
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setEditingHeroStat(null);
                      setHeroStatForm({ key: '', value: '', order: heroStats.length + 1, active: true });
                    }}
                    className="bg-pink-500 hover:bg-pink-600"
                  >
                    <Plus size={16} className="mr-2" /> Add Stat
                  </Button>
                </div>
              </div>

              {/* Hero Stat Form */}
              {(editingHeroStat !== null || heroStatForm.key !== '') && (
                <Card className="bg-slate-900/80 border-pink-500/50">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center justify-between">
                      <span>{editingHeroStat ? 'Edit Stat' : 'New Stat'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingHeroStat(null);
                          setHeroStatForm({ key: '', value: '', order: 0, active: true });
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <X size={20} />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Key (identifier)</label>
                        <Input
                          value={heroStatForm.key}
                          onChange={(e) => setHeroStatForm(prev => ({ ...prev, key: e.target.value }))}
                          className="bg-slate-800 border-slate-700 text-white"
                          placeholder="e.g., projects, clients, years"
                        />
                        <p className="text-xs text-gray-500 mt-1">Used for translations</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Value</label>
                        <Input
                          value={heroStatForm.value}
                          onChange={(e) => setHeroStatForm(prev => ({ ...prev, value: e.target.value }))}
                          className="bg-slate-800 border-slate-700 text-white"
                          placeholder="e.g., 500+, 200+, 10+"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Order</label>
                        <Input
                          type="number"
                          value={heroStatForm.order}
                          onChange={(e) => setHeroStatForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="statActive"
                        checked={heroStatForm.active}
                        onChange={(e) => setHeroStatForm(prev => ({ ...prev, active: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-pink-500"
                      />
                      <label htmlFor="statActive" className="text-gray-300">Active (visible on website)</label>
                    </div>

                    <Button onClick={saveHeroStat} disabled={loading} className="bg-pink-500 hover:bg-pink-600 w-full">
                      <Save size={16} className="mr-2" />
                      {loading ? 'Saving...' : 'Save Stat'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Stats Preview */}
              <Card className="bg-slate-900/80 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Preview (as shown on Hero)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
                    {heroStats.filter(s => s.active).map((stat) => (
                      <div key={stat.id} className="text-center p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                        <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-pink-400">
                          {stat.value}
                        </div>
                        <div className="text-sm text-gray-400 mt-1 capitalize">{stat.key}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Stats List */}
              <div className="grid gap-4">
                {heroStats.map((stat) => (
                  <Card key={stat.id} className={`bg-slate-900/80 border-slate-800 ${!stat.active && 'opacity-60'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                          <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                            <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-pink-400">
                              {stat.value}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-white font-semibold text-lg capitalize">{stat.key}</h3>
                            <p className="text-gray-400 text-sm">Order: {stat.order}</p>
                            {!stat.active && <span className="text-xs text-red-400">Hidden</span>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingHeroStat(stat);
                              setHeroStatForm({
                                key: stat.key,
                                value: stat.value,
                                order: stat.order,
                                active: stat.active
                              });
                            }}
                            className="border-slate-700 text-gray-400 hover:text-white"
                          >
                            <Edit size={16} className="mr-1" /> Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteHeroStat(stat.id)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {heroStats.length === 0 && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardContent className="p-12 text-center">
                    <BarChart3 className="mx-auto text-gray-600 mb-4" size={48} />
                    <p className="text-gray-400 mb-4">No hero stats yet. Add your first stat or seed default data.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
